import type { ModuleKey } from '@titularis/ui';
import capaEnam from './assets/capas/enam.webp';
import capaEssay from './assets/capas/essay.webp';
import capaLegislacao from './assets/capas/legislacao.webp';
import capaMagistratura1 from './assets/capas/magistratura-1.webp';
import capaMagistratura2 from './assets/capas/magistratura-2.webp';
import capaObjective from './assets/capas/objective.webp';
import capaOab from './assets/capas/oab.png';
  
/**
 * Módulos por concurso.
 *
 * Cada módulo (Enac, 1ª fase, Enam...) é um produto próprio, que poderá ser
 * assinado sozinho ou junto de outros. Aqui eles são agrupados pelo concurso a
 * que pertencem. Os de Cartórios estão no ar (têm `moduleKey`, que leva ao
 * estudo); os demais concursos ficam trancados até o lançamento - basta virar
 * `disponivel` e ligar o `moduleKey` de cada módulo quando chegar a hora.
 *
 * O `nome` é o rótulo curto, para o contexto do grupo; o nome completo do módulo
 * de Cartórios (ex.: "1ª fase - Cartórios") vive no `moduleLabel` do domínio.
 */
export interface SubModulo {
  chave: string;
  nome: string;
  /** Presente só nos módulos navegáveis (hoje, os de Cartórios). */
  moduleKey?: ModuleKey;
  /**
   * Rota própria, para o módulo que não é estudo de questões.
   *
   * A Legislação Comentada é **filha de Cartórios aqui na navegação e irmã dela
   * no escopo da taxonomia** (D-20): as disciplinas dela ("Nº 6.015") não são
   * disciplinas de prova nenhuma. As duas dimensões não coincidem, e é por isso
   * que ela não tem `moduleKey`.
   */
  rota?: string;
  /**
   * Capa do módulo (asset importado). Enquanto não houver arte, a Home mostra
   * um gradiente na cor do concurso. Para adicionar: ver assets/capas/README.md.
   */
  capa?: string;
  /**
   * Existe, está sendo comentado, e ainda não abre.
   *
   * **Diferente de `disponivel: false` do concurso inteiro** ("em breve", para
   * Magistratura e OAB, que não têm nem prova cadastrada). Aqui o módulo é de
   * Cartórios, tem lugar na barra e vai abrir - mas em 05/09/2026 a 1ª e a 2ª
   * fase tinham **zero questões publicadas**, e deixá-las clicáveis levava o
   * aluno a uma lista vazia com "Encontramos 0 questões". Uma tela vazia sem
   * explicação é pior que uma porta fechada com aviso. A 1ª fase abriu em
   * 15/10/2026; a 2ª continua aqui.
   */
  emPreparo?: boolean;
}

export interface GrandeModulo {
  chave: string;
  nome: string;
  disponivel: boolean;
  subModulos: SubModulo[];
}

/** As etapas que ainda estão sendo comentadas, para quem só tem a chave do módulo. */
export function moduloEmPreparo(chave: string | null | undefined): boolean {
  return GRANDES_MODULOS.some((concurso) =>
    concurso.subModulos.some((sub) => sub.moduleKey === chave && sub.emPreparo),
  );
}

export const GRANDES_MODULOS: GrandeModulo[] = [
  {
    chave: 'cartorios',
    nome: 'Cartórios',
    disponivel: true,
    subModulos: [
      /*
       * A capa do Enac vem da pasta pública, e não do bundle: o e-mail da
       * compra precisa de uma URL estável, e o Vite carimba um hash no nome de
       * tudo o que é importado. É a regra para capa de produto vendável - ver
       * assets/capas/README.md.
       */
      { chave: 'enac', nome: 'Enac', moduleKey: 'enac', capa: '/capas/enac.png' },
      /*
       * **Aberta em 15/10/2026**, com a primeira prova estadual publicada e
       * comentada (TJPA - IESES 2026, provimento). As outras estaduais entram
       * conforme são comentadas, sem passar por aqui: quem decide o que o aluno
       * alcança é a API, e o cadeado nunca foi controle de acesso - era a porta
       * para não deixar ninguém numa lista vazia.
       */
      { chave: 'objective', nome: '1ª fase', moduleKey: 'objective', capa: capaObjective },
      { chave: 'essay', nome: '2ª fase', moduleKey: 'essay', capa: capaEssay, emPreparo: true },
      /*
       * **De volta em 03/09/2026, com a reformulação da F14.**
       *
       * Ficou escondida desde 02/09 porque o formato antigo - PDF rasterizado
       * mais narração - seria substituído em semanas, e não se ensina ao aluno
       * uma tela que vai deixar de existir. O que ela abre agora é a lei do
       * acervo lida artigo a artigo, com o comentário autoral nos artigos que a
       * incidência elegeu.
       */
      {
        chave: 'legislacao',
        nome: 'Legislação Comentada',
        rota: '/legislacao',
        capa: capaLegislacao,
      },
    ],
  },
  {
    chave: 'magistratura',
    nome: 'Magistratura',
    disponivel: false,
    subModulos: [
      { chave: 'enam', nome: 'Enam', capa: capaEnam },
      { chave: 'magistratura-1', nome: '1ª fase', capa: capaMagistratura1 },
      { chave: 'magistratura-2', nome: '2ª fase', capa: capaMagistratura2 },
    ],
  },
  {
    chave: 'oab',
    nome: 'OAB',
    disponivel: false,
    subModulos: [
      { chave: 'oab-1', nome: '1ª fase', capa: capaOab },
      { chave: 'oab-2', nome: '2ª fase', capa: capaOab },
    ],
  },
];
